/**
 * RecommendationCards — shared upsell renderer for a gated feature.
 * Pulls plan / addon / pack data from ``useFeatureRecommendation`` and
 * lays them out as a horizontal row of cards (vertical on mobile).
 *
 * Each card shows: kind label, plan/addon/pack name, price in user's
 * currency (with USD fallback), and a CTA that deep-links to
 * ``/pricing?highlight=<id>`` so the user lands on the right tile.
 */
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Package, Zap } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { useProfile } from "../hooks/queries";
import { useFeatureRecommendation } from "../hooks/useFeatureRecommendation";
import { resolvePriceForUser, formatPrice } from "../lib/pricingResolver";

const KIND_META = {
  plan:  {
    es: "Plan", en: "Plan", pt: "Plano", icon: Sparkles,
    card: "border-violet-200 bg-violet-50/40",
    iconBox: "bg-violet-100 text-violet-700",
    label: "text-violet-700",
    cta: "border-violet-300 hover:bg-violet-100",
  },
  addon: {
    es: "Add-on", en: "Add-on", pt: "Add-on", icon: Zap,
    card: "border-emerald-200 bg-emerald-50/40",
    iconBox: "bg-emerald-100 text-emerald-700",
    label: "text-emerald-700",
    cta: "border-emerald-300 hover:bg-emerald-100",
  },
  pack:  {
    es: "Pack", en: "Pack", pt: "Pack", icon: Package,
    card: "border-amber-200 bg-amber-50/40",
    iconBox: "bg-amber-100 text-amber-700",
    label: "text-amber-700",
    cta: "border-amber-300 hover:bg-amber-100",
  },
};

function PriceLine({ pricing, userCurrency, lang, isPack }) {
  const resolved = resolvePriceForUser(pricing, userCurrency);
  if (!resolved) {
    return (
      <p className="text-xs text-slate-500 italic">
        {tx(lang, "See pricing", "Consultar precio", "Ver preço")}
      </p>
    );
  }
  const { entry, matched } = resolved;
  const formatted = formatPrice(entry.amount, entry.currency, lang);
  return (
    <div className="space-y-0.5">
      <p className="font-display text-lg font-bold text-slate-900">{formatted}</p>
      <p className="text-[11px] text-slate-500">
        {isPack
          ? tx(lang, "One-time payment", "Pago único", "Pagamento único")
          : tx(lang, "per month", "por mes", "por mês")}
        {!matched && ` · ${tx(lang, "USD fallback", "fallback USD", "fallback USD")}`}
      </p>
    </div>
  );
}

function RecommendationCard({ kind, data, featureKey, userCurrency, lang }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const isPack = kind === "pack";
  return (
    <Card
      className={`p-4 rounded-2xl flex flex-col gap-3 flex-1 min-w-[180px] ${meta.card}`}
      data-testid={`recommendation-${kind}-${featureKey}`}
    >
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${meta.iconBox}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] uppercase tracking-wider font-bold ${meta.label}`}>
            {tx(lang, meta.en, meta.es, meta.pt)}
          </p>
          <p className="text-sm font-semibold text-slate-900 truncate">{data.name}</p>
        </div>
      </div>
      <PriceLine pricing={data.pricing} userCurrency={userCurrency} lang={lang} isPack={isPack} />
      <Link to={`/pricing?highlight=${data.id}`} className="mt-auto">
        <Button
          size="sm"
          variant="outline"
          className={`w-full gap-1.5 rounded-full ${meta.cta}`}
          data-testid={`recommendation-cta-${kind}-${featureKey}`}
        >
          {isPack
            ? tx(lang, "View pack", "Ver pack", "Ver pack")
            : tx(lang, "View", "Ver", "Ver")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </Link>
    </Card>
  );
}

/**
 * Display-only component. Pure render of the three recommendation
 * slots. Hides empty kinds gracefully — if the catalog has no addon
 * matching the feature, only the plan + pack cards render.
 */
export function RecommendationCards({ featureKey }) {
  const { lang } = useLang();
  const { data, isLoading } = useFeatureRecommendation(featureKey);
  const { data: profile } = useProfile();
  // Currency follows the same chain the rest of the app uses: explicit
  // profile.currency wins; missing → USD (matches resolvePriceForUser).
  const userCurrency = profile?.currency || "USD";

  if (isLoading) {
    return (
      <div className="text-xs text-slate-400 italic" data-testid={`recommendations-loading-${featureKey}`}>
        {tx(lang, "Loading upgrade options…", "Cargando opciones…", "Carregando opções…")}
      </div>
    );
  }
  const recs = data?.recommendations || {};
  const cards = [
    recs.plan  && { kind: "plan",  data: recs.plan  },
    recs.addon && { kind: "addon", data: recs.addon },
    recs.pack  && { kind: "pack",  data: recs.pack  },
  ].filter(Boolean);

  if (cards.length === 0) {
    return (
      <p className="text-xs text-slate-500 italic" data-testid={`recommendations-empty-${featureKey}`}>
        {tx(lang,
          "No upgrade path configured yet — contact support.",
          "Sin opciones de upgrade configuradas — contacta a soporte.",
          "Sem opções de upgrade — contate o suporte.")}
      </p>
    );
  }
  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid={`recommendations-${featureKey}`}>
      {cards.map((c) => (
        <RecommendationCard
          key={c.kind}
          kind={c.kind}
          data={c.data}
          featureKey={featureKey}
          userCurrency={userCurrency}
          lang={lang}
        />
      ))}
    </div>
  );
}
